import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { isNativeSync } from "../native/pushNative";
// Client `api` CANONIQUE (factory unique) : il porte l'intercepteur CSRF
// (X-CSRF-Token) + withCredentials. Avant, ce composant créait son PROPRE
// axios.create SANS intercepteur CSRF → ses POST/PUT (read-all, push/subscribe)
// partaient sans header et étaient rejetés 403 par le middleware CSRF.
// Observé en prod (logs Railway 2026-06-28 : PUT /notifications/read-all → 403).
import { api } from "../context/AuthContext";

const BellIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>
  </svg>
);

// Son en attente si le navigateur bloque l'autoplay
let _pendingSound = false;

const _doPlaySound = () => {
  if (localStorage.getItem("sound_enabled") === "false") { return undefined; }
  const AudioCtx = globalThis.AudioContext || globalThis.webkitAudioContext;
  if (!AudioCtx) { return undefined; }
  const ctx = new AudioCtx();
  const note = (freq, t, dur) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.setValueAtTime(freq, t);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.15, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.start(t);
    o.stop(t + dur);
  };
  const t = ctx.currentTime;
  note(660, t, 0.12);
  note(880, t + 0.1, 0.18);
  return undefined;
};

const playNotificationSound = () => {
  if (localStorage.getItem("sound_enabled") === "false") { return undefined; }
  const AudioCtx = globalThis.AudioContext || globalThis.webkitAudioContext;
  if (!AudioCtx) { return undefined; }
  const ctx = new AudioCtx();
  // Si le contexte est suspendu (page en arrière-plan), stocker et jouer au prochain clic
  if (ctx.state === "suspended") {
    _pendingSound = true;
    return undefined;
  }
  _doPlaySound();
  return undefined;
};

// Jouer le son en attente dès la prochaine interaction utilisateur
if (typeof document !== "undefined") {
  const _playPending = () => {
    if (_pendingSound) {
      _pendingSound = false;
      _doPlaySound();
    }
  };
  document.addEventListener("click", _playPending, { passive: true });
  document.addEventListener("keydown", _playPending, { passive: true });
  document.addEventListener("touchstart", _playPending, { passive: true });
}

// Convertit une clé VAPID publique base64url en Uint8Array (format exigé par
// PushManager.subscribe). Sans cette conversion, l'abonnement échoue.
const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

// Clé publique VAPID récupérée au RUNTIME depuis le backend (dérivée de la clé
// privée → source unique, impossible d'être désynchronisée). Fallback sur la
// variable de build historique si l'endpoint n'est pas joignable. Mémoïsée.
let _vapidKeyCache;
const getVapidPublicKey = async () => {
  if (_vapidKeyCache !== undefined) return _vapidKeyCache;
  try {
    const r = await api.get("/push/vapid-public-key");
    if (r?.data?.public_key) { _vapidKeyCache = r.data.public_key; return _vapidKeyCache; }
  } catch (e) { /* backend indispo → fallback build */ }
  _vapidKeyCache = process.env.REACT_APP_VAPID_PUBLIC_KEY || null;
  return _vapidKeyCache;
};

// Diagnostic push lisible (RC) : renvoie l'état CLIENT réel pour comprendre
// pourquoi un appareil ne reçoit rien (support API, permission OS, abonnement,
// service worker). Garde-fou : timeout sur serviceWorker.ready (ne JAMAIS rester
// bloqué si le SW n'est pas actif).
export const getPushDiagnostic = async () => {
  const p = [];
  const hasN = typeof window !== "undefined" && "Notification" in window;
  const hasPM = typeof window !== "undefined" && "PushManager" in window;
  const hasSW = typeof navigator !== "undefined" && "serviceWorker" in navigator;
  p.push(`Notif:${hasN ? "ok" : "non"}`);
  p.push(`Push:${hasPM ? "ok" : "NON"}`);
  p.push(`SW:${hasSW ? "ok" : "non"}`);
  if (hasN) p.push(`perm:${Notification.permission}`);
  let sub = null;
  if (hasSW && hasPM) {
    try {
      const reg = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, rej) => setTimeout(() => rej(new Error("sw-timeout")), 2500)),
      ]);
      sub = await reg.pushManager.getSubscription();
    } catch (e) { p.push("SWready:timeout"); }
  }
  p.push(`abonné:${sub ? "oui" : "non"}`);
  return p.join(" · ");
};

// `fromUserGesture` : sur Android 13+ (TWA) et Chrome desktop, la permission de
// notification ne doit être DEMANDÉE qu'à l'intérieur d'un geste utilisateur
// (clic). Hors geste (au montage/login), Chrome applique l'UI « quieter » voire
// auto-bloque, et Lighthouse pénalise « notification permission on page load »
// (sources : web.dev/push-notifications-permissions-ux ; Lighthouse best-practices).
// En TWA Bubblewrap, la délégation POST_NOTIFICATIONS de l'AAB ne déclenche PAS
// le prompt à elle seule : c'est CET appel, sur clic, qui le relaie au natif.
// → Au montage : on ne fait que RE-souscrire silencieusement si déjà accordé
//   (jamais de prompt). Sur geste (cloche OU primer) : on peut demander.
// Retour : true si abonné (permission accordée + sub enregistrée), false sinon.
export const subscribeToPush = async (fromUserGesture = false) => {
  try {
    // Sur l'APP NATIVE (Capacitor), le push passe par FCM natif (pushNative.js),
    // pas par le Web Push (qui n'a ni canaux ni permission fiable). On ne touche
    // donc PAS au Web Push ici → évite la désync de permission Chrome/Android.
    if (isNativeSync()) return false;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
    if (!("Notification" in window)) return false;
    // Sans clé VAPID publique configurée, l'abonnement push ne peut PAS marcher
    // (l'ancien code passait applicationServerKey: null → exception silencieuse,
    // 0 subscription, fausse promesse). On ne demande même pas la permission
    // pour une capacité non configurée — on no-op proprement.
    const vapidKey = await getVapidPublicKey();
    if (!vapidKey) return false;
    // Permission déjà refusée → ne jamais re-prompter (Android verrouille après
    // refus ; insister dégrade l'UX). On sort proprement.
    if (Notification.permission === "denied") return false;
    // Hors geste utilisateur, on ne PROMPTE pas : on ne poursuit que si la
    // permission est déjà accordée (re-souscription silencieuse au login).
    if (Notification.permission !== "granted" && !fromUserGesture) return false;
    const permission = Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();
    if (permission !== "granted") return false;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
    // Via le client canonique `api` (intercepteur CSRF) : un `fetch` brut
    // n'attachait pas X-CSRF-Token → /push/subscribe (mutation authentifiée par
    // cookie) aurait été rejeté 403 par le middleware CSRF (même classe de bug
    // que read-all). `api` ajoute le header automatiquement.
    await api.post("/push/subscribe", {
      endpoint: sub.endpoint,
      keys: {
        auth: btoa(String.fromCharCode(...new Uint8Array(sub.getKey("auth")))),
        p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey("p256dh"))))
      }
    });
    return true;
  } catch(e) { /* Push subscription failed silently */ return false; }
};

const NotificationBell = ({ user, unreadCount = 0, onRead }) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  // Position du panneau calculée à l'ouverture (ancré sous la cloche, mais aligné
  // sur le BORD DROIT DU VIEWPORT en `fixed` → ne déborde jamais à gauche, quel
  // que soit le header — bug asso : le panneau 340px débordait l'écran à gauche).
  const [panelTop, setPanelTop] = useState(64);
  const [notifBusy, setNotifBusy] = useState(false);
  const prevUnreadRef = useRef(unreadCount);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { if (user) subscribeToPush(); }, [user]);

  // Le badge de l'icône de l'app N'EST PLUS ÉCRIT ICI (31/08/2026).
  // Cet effet s'exécute au MONTAGE avec la valeur initiale 0, donc avant toute
  // réponse réseau : `setAppBadge(0)` appelait `Badge.clear()` et effaçait le
  // badge natif à CHAQUE démarrage de l'app, panne ou pas. Deux exemplaires de
  // ce composant sont montés en même temps (DesktopHeader + MobileHeader, ce
  // dernier seulement caché en CSS par `lg:hidden`), donc il l'écrivait deux
  // fois. Et il ne comptait que les notifications, alors que le badge PWA
  // compte messages + notifications : les deux surfaces divergeaient.
  // Écrivain unique désormais : `updateBadge` dans hooks/useUnreadCounts.js,
  // qui n'écrit QUE sur des valeurs prouvées (jamais sur un « je ne sais pas »).
  useEffect(() => {
    if (unreadCount > prevUnreadRef.current) playNotificationSound();
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchNotifications = async () => {
    if (!user) return;
    setLoading(true);
    try {

      // Auth via httpOnly cookies
      const res = await api.get("/notifications?limit=10");
      const list = res.data.notifications || res.data || [];
      setNotifications(list);
      return list;
    } catch(e) { setNotifications([]); return []; }
    finally { setLoading(false); }
  };

  // Marque TOUT comme lu (bouton « Tout lire ») : read-all sans ids → tout le
  // backend ; on éteint les pastilles localement (dots) + le badge parent (onRead).
  const markAllRead = async () => {
    if (notifBusy) return;
    setNotifBusy(true);
    try {
      await api.put("/notifications/read-all", {});
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      if (onRead) onRead();
    } catch (e) { /* silencieux */ }
    finally { setNotifBusy(false); }
  };

  // Auto-test push (RC) : (1) tente d'abord d'ABONNER cet appareil dans le geste
  // du clic (déclenche le prompt natif), (2) si OK envoie un lot de notifications
  // de test, (3) si KO affiche un DIAGNOSTIC lisible (permission réelle, support
  // Push, état du service worker) — pour voir exactement pourquoi ça bloque.
  const sendTest = async () => {
    // App native : le token FCM est enregistré au démarrage (pushNative). On
    // envoie directement le test ; pas de Web Push à activer.
    if (isNativeSync()) {
      try {
        const r = await api.post("/push/test");
        toast.success(`Test envoyé (${r.data?.sent || 0}). Regardez vos notifications.`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Activez les notifications de Gively dans les réglages Android.");
      }
      return;
    }
    const ok = await subscribeToPush(true);
    const diag = await getPushDiagnostic();
    if (!ok) {
      const denied = typeof Notification !== "undefined" && Notification.permission === "denied";
      toast.error(
        (denied
          ? "Notifications BLOQUÉES sur cet appareil. Réglages Android → Applications → Gively → Notifications → Autoriser, puis réessayez."
          : "Activation impossible (autorisation non accordée).") + `  ·  [${diag}]`,
        { duration: 12000 }
      );
      return;
    }
    try {
      const r = await api.post("/push/test");
      toast.success(`Test envoyé (${r.data?.sent || 0}). Regardez vos notifications.`);
    } catch (e) {
      toast.error((e?.response?.data?.detail || "Erreur d'envoi") + `  ·  [${diag}]`, { duration: 12000 });
    }
  };

  const handleOpen = async () => {
    const opening = !open;
    if (opening && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPanelTop(Math.round(r.bottom + 8));
    }
    setOpen(!open);
    if (!open) {
      // Geste utilisateur (clic) : c'est le SEUL moment où l'on a le droit de
      // demander la permission de notification (Android 13+/Chrome bloquent un
      // prompt hors geste). Si pas encore accordée, on déclenche le prompt natif
      // POST_NOTIFICATIONS via la délégation TWA. No-op si déjà accordé/refusé.
      subscribeToPush(true);
      // Audit LUCAS N1 — on ne marque lues QUE les notifications réellement
      // affichées (la cloche n'en montre que 10) : sinon les notifs non
      // affichées étaient marquées lues sans avoir été vues. On récupère la
      // liste fraîche, puis on envoie ses ids à read-all.
      const fresh = await fetchNotifications();
      try {
        const ids = (fresh || [])
          .filter((n) => !n.is_read)
          .map((n) => n.notification_id)
          .filter(Boolean);
        // Si rien d'affiché n'est non-lu, on ne touche à rien (ne pas envoyer
        // un read-all vide qui marquerait TOUT lu, y compris les non affichées).
        if (ids.length) {
          await api.put("/notifications/read-all", { ids });
          if (onRead) onRead();
        }
      } catch(e) {}
    }
  };

  const handleNotifClick = (notif) => {
    setOpen(false);
    if (notif.link) navigate(notif.link);
    else if (notif.type === "message") navigate("/messages");
    else if (notif.type === "item_sold") navigate("/mes-ventes");
    else if (notif.type === "payment_confirmed" || notif.type === "refund_processed") navigate("/mes-achats");
  };

  // Icônes alignées sur les vrais notification_type produits par le backend
  // (audit #6 : item_sold/payment_confirmed/donation_received/… tombaient sur 🔔 ;
  // route morte /mon-espace remplacée par /mes-ventes · /mes-achats).
  const getIcon = (type) => ({
    message:"💬", badge:"🏆", system:"📢", gamification:"⭐", saved_search:"🔔",
    offer:"💰", offer_accepted:"✅", offer_refused:"❌", payment_setup_needed:"⚙️",
    item_sold:"🎉", payment_confirmed:"💳", donation_received:"💜", refund_processed:"💸",
    parcel_shipped:"📦", payout_completed:"💸", receipt_confirmed:"✅", dispute_resolved:"⚖️",
    rental_handover:"📅", rental_return:"📦", rental_done:"✅", rental_dispute:"⚠️",
    rental_deposit_held:"🔒", rental_deposit_released:"🔓", rental_return_overdue:"⏰",
    service_request:"📅", service_counter:"🔄", service_accepted:"✅", service_refused:"❌",
    sale:"🎉", purchase:"✅", payment:"💰",
  }[type] || "🔔");

  const timeAgo = (d) => {
    try {
      const s = (Date.now() - new Date(d).getTime()) / 1000;
      if (s < 60) return "À l'instant";
      if (s < 3600) return `${Math.floor(s/60)}min`;
      if (s < 86400) return `${Math.floor(s/3600)}h`;
      return `${Math.floor(s/86400)}j`;
    } catch { return ""; }
  };

  if (!user) return null;

  return (
    <div ref={ref} style={{ position:"relative", display:"inline-block" }}>
      <button onClick={handleOpen} title="Notifications" aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} non lue${unreadCount > 1 ? "s" : ""})` : ""}`} aria-haspopup="dialog" aria-expanded={open} style={{ position:"relative", background: open ? "#f0f9ff" : "transparent", border:"none", borderRadius:"50%", width:"40px", height:"40px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color: open ? "#1DA1F2" : "#64748b", transition:"all 0.15s" }}>
        <BellIcon />
        {unreadCount > 0 && (
          <span style={{ position:"absolute", top:"2px", right:"2px", background:"#EF4444", color:"white", fontSize:"10px", fontWeight:"800", borderRadius:"9999px", minWidth:"17px", height:"17px", display:"flex", alignItems:"center", justifyContent:"center", padding:"0 3px", boxShadow:"0 0 0 2px white", animation:"pulse 2s infinite" }}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{ position:"fixed", top: panelTop, right:8, left:"auto", width:"min(340px, calc(100vw - 16px))", maxHeight:"min(75vh, 560px)", background:"white", borderRadius:"16px", boxShadow:"0 20px 60px rgba(0,0,0,0.15)", border:"1px solid #e2e8f0", overflow:"hidden", zIndex:9999, display:"flex", flexDirection:"column" }}>
          <div style={{ padding:"14px 16px 10px", borderBottom:"1px solid #f1f5f9", display:"flex", alignItems:"center", justifyContent:"space-between", gap:"8px", flexShrink:0 }}>
            <span style={{ fontWeight:"700", fontSize:"15px", color:"#0f172a", whiteSpace:"nowrap" }}>🔔 Notifications</span>
            {notifications.some((n) => !n.is_read) ? (
              <button type="button" onClick={markAllRead} disabled={notifBusy}
                style={{ all:"unset", cursor: notifBusy ? "default" : "pointer", color:"#1DA1F2", fontSize:"12.5px", fontWeight:"600", whiteSpace:"nowrap", opacity: notifBusy ? 0.5 : 1 }}>
                {notifBusy ? "…" : "Tout lire"}
              </button>
            ) : null}
          </div>
          <div style={{ flex:1, minHeight:0, overflowY:"auto" }}>
            {loading ? (
              <div style={{ padding:"30px", textAlign:"center", color:"#94a3b8" }}><div style={{ fontSize:"24px", marginBottom:"8px" }}>⏳</div>Chargement...</div>
            ) : notifications.length === 0 ? (
              <div style={{ padding:"40px 20px", textAlign:"center", color:"#94a3b8" }}><div style={{ fontSize:"36px", marginBottom:"8px" }}>🔔</div><p style={{ margin:0, fontSize:"14px" }}>Aucune notification</p></div>
            ) : notifications.map((n, i) => (
              // Phase 13.82 (audit a11y) — converti div onClick en
              // <button> pour navigation clavier (Enter/Espace) + role
              // semantique correct.
              <button type="button" key={n.notification_id || i} onClick={() => handleNotifClick(n)}
                style={{ all:"unset", width:"100%", boxSizing:"border-box", padding:"12px 18px", display:"flex", gap:"12px", alignItems:"flex-start", cursor:"pointer", background: !n.is_read ? "#f0f9ff" : "white", borderBottom:"1px solid #f8fafc", transition:"background 0.15s", textAlign:"left" }}
                onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                onMouseLeave={e => e.currentTarget.style.background = !n.is_read ? "#f0f9ff" : "white"}>
                <span style={{ fontSize:"22px", flexShrink:0, marginTop:"1px" }} aria-hidden="true">{getIcon(n.type)}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ margin:"0 0 2px", fontSize:"13px", fontWeight: !n.is_read ? "600" : "400", color:"#0f172a", lineHeight:1.4 }}>{n.title || n.message || "Nouvelle notification"}</p>
                  {/* Corps actionnable : le backend écrit le détail dans `message`
                      (pas `body`) — montant d'offre, "Réglez via le bouton Payer"…
                      On l'affiche en 2e ligne dès qu'il diffère du titre (sinon il
                      était invisible). Clamp 2 lignes au lieu d'1. */}
                  {(n.body || (n.title && n.message && n.message !== n.title)) && (
                    <p style={{ margin:"0 0 4px", fontSize:"12px", color:"#64748b", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{n.body || n.message}</p>
                  )}
                  <span style={{ fontSize:"11px", color:"#94a3b8" }}>{timeAgo(n.created_at)}</span>
                </div>
                {!n.is_read && <span style={{ width:"8px", height:"8px", borderRadius:"50%", background:"#1DA1F2", flexShrink:0, marginTop:"6px" }} aria-label="Non lu" />}
              </button>
            ))}
          </div>
          {notifications.length > 0 && (
            <button type="button" onClick={() => { setOpen(false); navigate("/notifications"); }}
              style={{ all:"unset", width:"100%", boxSizing:"border-box", padding:"12px", textAlign:"center", borderTop:"1px solid #f1f5f9", cursor:"pointer", color:"#1DA1F2", fontSize:"13px", fontWeight:"600" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f0f9ff"}
              onMouseLeave={e => e.currentTarget.style.background = "white"}>
              Voir toutes les notifications →
            </button>
          )}
          {/* Auto-test push (vérifier que les notifications arrivent vraiment). */}
          <button type="button" onClick={sendTest}
            style={{ all:"unset", width:"100%", boxSizing:"border-box", padding:"10px", textAlign:"center", borderTop:"1px solid #f1f5f9", cursor:"pointer", color:"#94a3b8", fontSize:"12px", fontWeight:"500" }}
            onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
            onMouseLeave={e => e.currentTarget.style.background = "white"}>
            🔔 Tester mes notifications
          </button>
        </div>
      )}
      <style>{`@keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.15)} }`}</style>
    </div>
  );
};

export default NotificationBell;
